import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import axios from "axios";

import { API } from "@/lib/api";

export function DuplicatesModal({ open, onClose, pipelineId, pipelineName, onRefresh }) {
  const [data, setData] = useState({ total_groups: 0, duplicates: [] });
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState("");
  const [cleaning, setCleaning] = useState(false);
  const [confirmClean, setConfirmClean] = useState(false);

  useEffect(() => {
    if (open && pipelineId) {
      loadDuplicates();
      setConfirmClean(false);
    }
  }, [open, pipelineId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDuplicates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contacts/duplicates?pipeline_id=${pipelineId}`);
      setData(res.data);
    } catch {
      setData({ total_groups: 0, duplicates: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (contactId, nome) => {
    if (!window.confirm(`Remover "${nome}"?`)) return;
    setRemoving(contactId);
    try {
      await axios.delete(`${API}/contacts/${contactId}`);
      toast.success(`${nome} removido`);
      loadDuplicates();
      onRefresh();
    } catch {
      toast.error("Erro ao remover");
    } finally {
      setRemoving("");
    }
  };

  const totalDuplicates = data.duplicates.reduce((sum, g) => sum + g.count - 1, 0);

  const handleCleanAll = async () => {
    if (!confirmClean) {
      setConfirmClean(true);
      return;
    }
    setCleaning(true);
    try {
      const res = await axios.post(`${API}/contacts/duplicates/clean?pipeline_id=${pipelineId}`);
      const { removed, kept } = res.data;
      toast.success(`${removed} duplicados removidos. ${kept} registros preservados.`);
      setConfirmClean(false);
      loadDuplicates();
      onRefresh();
    } catch {
      toast.error("Erro ao limpar duplicados");
    } finally {
      setCleaning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); setConfirmClean(false); } }}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-zinc-100 text-base font-semibold">
            Duplicados — {pipelineName}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
          {loading ? (
            <p className="text-xs text-zinc-500 text-center py-8">Verificando...</p>
          ) : data.total_groups === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm text-zinc-400">Nenhum duplicado encontrado</p>
              <p className="text-xs text-zinc-600 mt-1">Todos os telefones sao unicos nesta campanha</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Summary + bulk action */}
              <div className="bg-amber-500/10 rounded-lg p-3 space-y-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                  <p className="text-xs text-amber-400">
                    {data.total_groups} grupo{data.total_groups > 1 ? "s" : ""} · {totalDuplicates} duplicado{totalDuplicates > 1 ? "s" : ""} para remover
                  </p>
                </div>

                {!confirmClean ? (
                  <Button
                    data-testid="clean-all-btn"
                    onClick={handleCleanAll}
                    disabled={cleaning}
                    className="w-full bg-amber-500 text-zinc-950 hover:bg-amber-600 h-8 text-xs font-semibold"
                  >
                    <Zap className="h-3.5 w-3.5 mr-1.5" />
                    Remover {totalDuplicates} duplicados automaticamente
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <div className="bg-zinc-900 rounded-md p-2.5">
                      <p className="text-xs text-zinc-300 mb-1 font-medium">Regra de preservacao:</p>
                      <p className="text-[11px] text-zinc-500 leading-relaxed">
                        Para cada grupo, o registro com mais historico e interacoes sera mantido. Em caso de empate, o mais antigo prevalece. Os demais serao removidos permanentemente.
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setConfirmClean(false)}
                        className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 h-8 text-xs"
                      >
                        Cancelar
                      </Button>
                      <Button
                        data-testid="confirm-clean-btn"
                        onClick={handleCleanAll}
                        disabled={cleaning}
                        className="flex-1 bg-red-600 hover:bg-red-500 text-white h-8 text-xs font-semibold disabled:opacity-50"
                      >
                        {cleaning ? "Limpando..." : `Confirmar remocao de ${totalDuplicates}`}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Individual groups (show first 20) */}
              {data.duplicates.slice(0, 20).map((group, gi) => (
                <div key={gi} className="bg-zinc-900 rounded-lg p-3">
                  <p className="text-xs text-zinc-500 mb-2 font-medium">
                    {group.contacts[0]?.telefone} ({group.count} registros)
                  </p>
                  <div className="space-y-1.5">
                    {group.contacts.map((c, ci) => (
                      <div
                        key={c.id}
                        className={`flex items-center justify-between px-3 py-2 rounded-md ${ci === 0 ? "bg-zinc-800" : "bg-zinc-800/50"}`}
                      >
                        <div>
                          <span className="text-sm text-zinc-200">{c.nome}</span>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[10px] text-zinc-500 uppercase">{c.etapa}</span>
                            {c.responsavel && <span className="text-[10px] text-zinc-600">{c.responsavel}</span>}
                          </div>
                        </div>
                        {ci > 0 && (
                          <Button
                            size="sm"
                            onClick={() => handleRemove(c.id, c.nome)}
                            disabled={removing === c.id}
                            className="bg-red-900/30 hover:bg-red-900/50 text-red-400 h-7 text-xs px-2"
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            {removing === c.id ? "..." : "Remover"}
                          </Button>
                        )}
                        {ci === 0 && (
                          <span className="text-[10px] text-zinc-600 bg-zinc-700 px-2 py-0.5 rounded">manter</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {data.duplicates.length > 20 && (
                <p className="text-xs text-zinc-600 text-center py-2">
                  + {data.duplicates.length - 20} grupos adicionais. Use a limpeza automatica acima.
                </p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
